import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Radio,
  Divider,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  FormControlLabel,
  Switch,
  Grid,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  Backdrop,
  Fade,
  Chip,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import { 
  CheckCircleOutline as CheckCircleOutlineIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import {
  verifyFitnessCenter,
  updatePartnerProfile,
  getCurrentUser,
  getFitnessCenterById,
  logout
} from '../services/api';
import LanguageSwitcher from '../components/LanguageSwitcher';
import tuttoGymLogo from '../images/tuttogym.jpeg';

// Load Google Maps script
const loadGoogleMapsScript = (callback) => {
  if (window.google && window.google.maps) {
    callback();
    return;
  }

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY';
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
  script.async = true;
  script.defer = true;
  script.onload = callback;
  document.head.appendChild(script);
};

const PartnerOnboarding = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [createNew, setCreateNew] = useState(false);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showNotFoundDialog, setShowNotFoundDialog] = useState(false);
  const [isRegisteredInGoogle, setIsRegisteredInGoogle] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    website: '',
    mainPhoto: '',
    otherPhotos: [],
    type: 'Gym', // Default facility type
    latitude: 0,
    longitude: 0,
    city: '',
    postalCode: ''
  });
  
  const addressInputRef = useRef(null);
  const placesAutocomplete = useRef(null);
  
  // Load user data on mount
  useEffect(() => {
    const user = getCurrentUser();
    if (!user || user.role !== 'partner') {
      navigate('/panel');
      return;
    }
    setCurrentUser(user);
    
    // Load Google Maps API
    loadGoogleMapsScript(() => {
      setGoogleMapsLoaded(true);
    });
  }, [navigate]);
  
  // Initialize Google Places Autocomplete when Google Maps is loaded
  useEffect(() => {
    if (googleMapsLoaded && addressInputRef.current) {
      placesAutocomplete.current = new window.google.maps.places.Autocomplete(
        addressInputRef.current,
        { types: ['address'] }
      );
      
      // Set up listener for place selection
      placesAutocomplete.current.addListener('place_changed', () => {
        const place = placesAutocomplete.current.getPlace();
        
        if (!place.geometry) {
          return;
        }
        
        // Extract address components
        let city = '';
        let postalCode = '';
        
        place.address_components.forEach(component => {
          if (component.types.includes('locality')) {
            city = component.long_name;
          } else if (component.types.includes('postal_code')) {
            postalCode = component.long_name;
          }
        });
        
        // Update form data with selected place
        const updatedFormData = {
          ...formData,
          address: place.formatted_address,
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
          city,
          postalCode
        };
        
        setFormData(updatedFormData);
        
        // Just cache the updated form data but don't trigger verification yet
        handleSearch(updatedFormData);
      });
    }
  }, [googleMapsLoaded, formData.name]);
  
  // Handle moving to next step
  const handleNextStep = async () => {
    if (activeStep === 0) {
      if (!formData.name || !formData.address) {
        return;
      }
      
      // Show loading indicator
      setSearching(true);
      console.log('Buscando coincidencias para:', {
        name: formData.name,
        address: formData.address
      });
      
      try {
        // Explicitly verify fitness center when moving to next step
        const result = await verifyFitnessCenter(formData.name, formData.address);
        console.log('Resultado de la verificación:', result);
        
        if (result.status === 'success') {
          const matchingFacilities = result.data || [];
          console.log(`Se encontraron ${matchingFacilities.length} instalaciones similares`);
          
          setSearchResults(matchingFacilities);
          setSearchPerformed(true);
          setActiveStep(1); // Move to facility selection step
        } else {
          // API error, still move to next step but with empty results
          console.error('Error en la respuesta de la API:', result);
          setSearchResults([]);
          setSearchPerformed(true);
          setActiveStep(1);
        }
      } catch (error) {
        console.error('Error completo al verificar el centro de fitness:', error);
        setSearchResults([]);
        setSearchPerformed(true);
        setActiveStep(1);
      } finally {
        setSearching(false);
      }
    } else if (activeStep === 1) {
      // From facility selection step to additional info step
      if (selectedFacility) {
        console.log('Instalación seleccionada:', selectedFacility);
        // Pre-fill form with selected facility data
        setFormData({
          name: selectedFacility.name || formData.name,
          address: selectedFacility.address || formData.address,
          phone: selectedFacility.phone || '',
          website: selectedFacility.website || '',
          mainPhoto: selectedFacility.main_photo || '',
          otherPhotos: selectedFacility.other_photos || [],
          type: selectedFacility.type || 'Gym',
          latitude: selectedFacility.latitude || formData.latitude,
          longitude: selectedFacility.longitude || formData.longitude,
          city: selectedFacility.city || formData.city,
          postalCode: selectedFacility.postal_code || formData.postalCode
        });
      } else {
        console.log('Creando nueva instalación, no se ha seleccionado una existente');
      }
      setActiveStep(2);
    } else {
      setActiveStep(activeStep + 1);
    }
  };
  
  // Function to search for existing facilities
  const handleSearch = (data) => {
    const { name, address } = data;
    
    // Clear any pending timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Only search if we have name or address
    if (!name && !address) {
      setSearchResults([]);
      return;
    }
    
    // We'll only cache the search parameters, but won't actually search
    // until the user clicks "Next"
    setSearchPerformed(false);
  };
  
  // Handle input changes in the form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Update form data
    const updatedFormData = {
      ...formData,
      [name]: value
    };
    
    setFormData(updatedFormData);
    
    // We'll just cache the form data, but won't trigger search right away
    if (name === 'name' || name === 'address') {
      handleSearch(updatedFormData);
    }
  };
  
  // Handle selection of an existing facility
  const handleSelectFacility = (facility) => {
    setSelectedFacility(facility);
    setIsRegisteredInGoogle(true);
    setCreateNew(false);
  };
  
  // Handle creation of a new facility
  const handleCreateNew = () => {
    setCreateNew(true);
    setSelectedFacility(null);
  };

  // Handle Google registration response
  const handleGoogleRegistrationResponse = (isRegistered) => {
    setIsRegisteredInGoogle(isRegistered);
    setSelectedFacility(null);
    setCreateNew(true);
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!currentUser || !currentUser.id) {
      return;
    }
    
    setLoading(true);
    
    try {
      let response;
      
      if (selectedFacility) {
        // Usar datos de la instalación existente en vez de su ID
        const facilityData = {
          ...formData,
          name: selectedFacility.name || formData.name,
          address: selectedFacility.address || formData.address,
          type: selectedFacility.type || formData.type
        };
        
        response = await updatePartnerProfile(
          currentUser.id,
          facilityData
        );
      } else {
        // Create a new facility
        response = await updatePartnerProfile(
          currentUser.id,
          formData
        );
      }
      
      if (response.status === 'success') {
        // Marcamos que el perfil está completado sin depender de fitnessCenterId
        localStorage.setItem('profileComplete', 'true');
        
        // Redireccionar directamente al dashboard sin diálogo
        navigate('/panel/partner-dashboard');
      } else {
        // Incluso si hay un error, marcamos el perfil como completo para que
        // el usuario no tenga que completar el formulario nuevamente
        console.warn('Warning: Backend reported error but continuing anyway:', response.message);
        localStorage.setItem('profileComplete', 'true');
        
        // Redireccionar al dashboard
        navigate('/panel/partner-dashboard');
      }
    } catch (error) {
      console.error('Error saving partner profile:', error);
      setLoading(false);
      
      // A pesar del error, marcamos el perfil como completo para evitar problemas
      localStorage.setItem('profileComplete', 'true');
      
      // Redirigir al dashboard incluso después de un error
      navigate('/panel/partner-dashboard');
    }
  };
  
  // Back button handler
  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };
  
  // Handle opening/closing settings menu
  const handleSettingsMenuOpen = (event) => {
    setSettingsMenuAnchor(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsMenuAnchor(null);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      // Close the menu if it exists
      handleSettingsMenuClose();
      
      await logout();
      
      // Add a delay for the animation
      setTimeout(() => {
        setLoggingOut(false);
        // Redirect to main page
        window.location.replace('/');
      }, 1000);
    } catch (error) {
      console.error('Logout failed:', error);
      setLoggingOut(false);
    }
  };
  
  // Render steps
  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('partner.onboarding.facilityInfoTitle', 'Información de la Instalación')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {t('partner.onboarding.facilityInfoDesc', 'Por favor ingresa el nombre y dirección de tu instalación deportiva. Verificaremos si ya existe en nuestra base de datos.')}
            </Typography>
            
            <TextField
              fullWidth
              label={t('partner.onboarding.facilityName', 'Nombre de la Instalación')}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              margin="normal"
              required
              helperText={t('partner.onboarding.facilityNameHelp', 'Nombre de tu gimnasio, centro deportivo, estudio, etc.')}
            />
            
            <TextField
              fullWidth
              label={t('partner.onboarding.address', 'Dirección')}
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              margin="normal"
              required
              helperText={t('partner.onboarding.addressHelp', 'Dirección completa con calle, ciudad y código postal')}
              inputRef={addressInputRef}
            />
            
            {searching && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 1 }}>
                  {t('partner.onboarding.searching', 'Buscando...')}
                </Typography>
              </Box>
            )}
            
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleNextStep}
                disabled={!formData.name || !formData.address || searching}
              >
                {searching ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    {t('partner.onboarding.searching', 'Buscando...')}
                  </>
                ) : (
                  t('common.next', 'Siguiente')
                )}
              </Button>
            </Box>
          </Box>
        );
      
      case 1: // Facility Selection Step
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('partner.onboarding.selectFacility', 'Selecciona tu Instalación')}
            </Typography>
            
            {searchResults.length > 0 ? (
              <Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t('partner.onboarding.selectMatchPrompt', 'Encontramos estas instalaciones que coinciden con tu búsqueda. ¿Es alguna de ellas tu instalación?')}
                </Typography>
                
                <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
                  <RadioGroup 
                    value={selectedFacility ? selectedFacility.id : 'none'}
                    onChange={(e, value) => {
                      if (value === 'none') {
                        setSelectedFacility(null);
                      } else {
                        const selected = searchResults.find(f => f.id.toString() === value);
                        if (selected) handleSelectFacility(selected);
                      }
                    }}
                  >
                    {searchResults.slice(0, 3).map((facility) => (
                      <Paper key={facility.id} elevation={1} sx={{ mb: 2, p: 2 }}>
                        <FormControlLabel 
                          value={facility.id.toString()} 
                          control={<Radio />} 
                          label={
                            <Box>
                              <Typography variant="subtitle1" component="div">
                                {facility.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {facility.address}
                              </Typography>
                              {facility.type && (
                                <Typography variant="body2" color="text.secondary">
                                  {t('partner.onboarding.type', 'Tipo')}: {facility.type}
                                </Typography>
                              )}
                            </Box>
                          }
                          sx={{ width: '100%', m: 0 }}
                        />
                      </Paper>
                    ))}
                    
                    <Paper elevation={1} sx={{ mb: 2, p: 2 }}>
                      <FormControlLabel 
                        value="none" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography variant="subtitle1" component="div">
                              {t('partner.onboarding.noneOfThese', 'Ninguna de estas')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('partner.onboarding.createNewFacility', 'Mi instalación no está en esta lista')}
                            </Typography>
                          </Box>
                        }
                        sx={{ width: '100%', m: 0 }}
                      />
                    </Paper>
                  </RadioGroup>
                </FormControl>
                
                {selectedFacility === null && (
                  <Box sx={{ mt: 3, mb: 2 }}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">
                        {t('partner.onboarding.isInGoogle', '¿Está tu instalación registrada en Google Maps?')}
                      </FormLabel>
                      <RadioGroup 
                        row
                        value={isRegisteredInGoogle === null ? '' : isRegisteredInGoogle.toString()}
                        onChange={(e) => handleGoogleRegistrationResponse(e.target.value === 'true')}
                      >
                        <FormControlLabel 
                          value="true" 
                          control={<Radio />} 
                          label={t('partner.onboarding.isRegistered', 'Sí, está registrada')} 
                        />
                        <FormControlLabel 
                          value="false" 
                          control={<Radio />} 
                          label={t('partner.onboarding.notRegistered', 'No, no está registrada')} 
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>
                )}
              </Box>
            ) : (
              <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  {t('partner.onboarding.noMatchesFound', 'No pudimos encontrar instalaciones que coincidan con tu búsqueda.')}
                </Alert>
                
                <Box sx={{ mt: 3, mb: 2 }}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">
                      {t('partner.onboarding.isInGoogle', '¿Está tu instalación registrada en Google Maps?')}
                    </FormLabel>
                    <RadioGroup 
                      row
                      value={isRegisteredInGoogle === null ? '' : isRegisteredInGoogle.toString()}
                      onChange={(e) => handleGoogleRegistrationResponse(e.target.value === 'true')}
                    >
                      <FormControlLabel 
                        value="true" 
                        control={<Radio />} 
                        label={t('partner.onboarding.isRegistered', 'Sí, está registrada')} 
                      />
                      <FormControlLabel 
                        value="false" 
                        control={<Radio />} 
                        label={t('partner.onboarding.notRegistered', 'No, no está registrada')} 
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>
            )}
            
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                {t('common.back', 'Atrás')}
              </Button>
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleNextStep}
                disabled={
                  (selectedFacility === null && isRegisteredInGoogle === null) ||
                  (searchResults.length > 0 && selectedFacility === null && isRegisteredInGoogle === null)
                }
              >
                {t('common.next', 'Siguiente')}
              </Button>
            </Box>
          </Box>
        );
        
      case 2: // Additional Info Step
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {selectedFacility 
                ? t('partner.onboarding.editFacilityInfoTitle', 'Editar Información de la Instalación') 
                : t('partner.onboarding.additionalInfoTitle', 'Información Adicional')}
            </Typography>
            
            {isRegisteredInGoogle === false && (
              <Alert severity="info" sx={{ mb: 3 }}>
                {t('partner.onboarding.notInGoogleInfo', 'Ya que tu instalación no está registrada en Google, por favor proporciona la mayor cantidad de detalles posibles.')}
              </Alert>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('partner.onboarding.phone', 'Número de Teléfono')}
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('partner.onboarding.website', 'Sitio Web')}
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  margin="normal"
                  placeholder="https://"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('partner.onboarding.type', 'Tipo de Instalación')}
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  margin="normal"
                  helperText={t('partner.onboarding.typeHelp', 'Ej., Gimnasio, Centro Deportivo, Estudio de Yoga, etc.')}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('partner.onboarding.mainPhoto', 'URL de Foto Principal')}
                  name="mainPhoto"
                  value={formData.mainPhoto}
                  onChange={handleInputChange}
                  margin="normal"
                  helperText={t('partner.onboarding.photoHelp', 'Enlace directo a la foto principal de tu instalación')}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                {t('common.back', 'Atrás')}
              </Button>
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {loading ? 
                  t('common.saving', 'Guardando...') : 
                  t('common.finishRegistration', 'Terminar Registro')}
              </Button>
            </Box>
          </Box>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Logout Animation Backdrop */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }}
        open={loggingOut}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6">{t('auth.loggingOut', 'Cerrando sesión...')}</Typography>
      </Backdrop>

      {/* App Bar with title and user info */}
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar>
          {/* Logo */}
          <Box
            component="img"
            src={tuttoGymLogo}
            alt="TuttoGym Logo"
            sx={{ height: 40, mr: 2 }}
          />
          
          {/* Dashboard title with partner name */}
          <Typography variant="h6" component="div">
            {currentUser?.partnerName || t('dashboard.partner.defaultName', 'Partner')} - {t('partner.onboarding.completingProfile', 'Completando Perfil')}
          </Typography>
          
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Language selector */}
          <LanguageSwitcher />
          
          {/* User email */}
          {currentUser && (
            <Typography variant="body2" sx={{ mx: 2 }}>
              {currentUser.email}
            </Typography>
          )}
          
          {/* Settings button */}
          <IconButton 
            color="inherit" 
            onClick={handleSettingsMenuOpen}
            size="large"
            title={t('common.settings', 'Configuración')}
          >
            <SettingsIcon />
          </IconButton>
          
          <Menu
            anchorEl={settingsMenuAnchor}
            open={Boolean(settingsMenuAnchor)}
            onClose={handleSettingsMenuClose}
          >
            <MenuItem onClick={handleLogout} disabled={loggingOut}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('common.logout', 'Cerrar Sesión')}</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, mb: 8, width: '100%' }}>
        <Paper elevation={3}>
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h5" component="h1" gutterBottom>
              {t('partner.onboarding.title', 'Completa tu Perfil')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('partner.onboarding.subtitle', 'Por favor proporciona información sobre tu instalación deportiva para completar tu perfil de partner.')}
            </Typography>
          </Box>
          
          <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 2, px: 4 }}>
            <Step>
              <StepLabel>{t('partner.onboarding.step1', 'Información Básica')}</StepLabel>
            </Step>
            <Step>
              <StepLabel>{t('partner.onboarding.step2', 'Selección de Instalación')}</StepLabel>
            </Step>
            <Step>
              <StepLabel>{t('partner.onboarding.step3', 'Información Adicional')}</StepLabel>
            </Step>
          </Stepper>
          
          {renderStep()}
        </Paper>
      </Box>
    </Box>
  );
};

export default PartnerOnboarding; 